import { useState } from 'react';
import * as Yup from 'yup';
import Button from '../../../../components/form-control/Button';
import SelectDropdown from '../../../../components/form-control/SelectDropdown';
import { Policy } from '../../../../models/policy/Policy';
import Input from '../../../../components/form-control/Input';
import { Formik } from 'formik';
import { getFormattedPhoneNumber, getPhoneNumberFromShortCodeFormat, removeUnderscoresAndDashes, showToast } from '../../../../utils/miscFunctions';
import { sharePolicyAgreementDocumentLink } from '../../../../api/requests';
import { POLICY_DOCUMENT_TYPES } from '../../../../resources/enums';
import useResource from '../../../../hooks/resourcesHook';

type ShareDocumentModalProps = {
  policy: Policy;
  onClose: () => void;
  onProceed?: (url: string) => void;
  isPaymentAgreement?: boolean;
};

const documentList = [
  POLICY_DOCUMENT_TYPES.RENEWAL_NOTICE,
  // Hidden per business request — the share flow is currently renewal-notice only.
  // The payment agreement path pre-sets its type and never shows this dropdown.
  // POLICY_DOCUMENT_TYPES.LOAN_AGREEMENT,
  // POLICY_DOCUMENT_TYPES.PAYMENT_LOAN_AGREEMENT,
  // POLICY_DOCUMENT_TYPES.INSURANCE_CERTIFICATE,
  // POLICY_DOCUMENT_TYPES.PAYMENT_AGREEMENT,
  // POLICY_DOCUMENT_TYPES.CONTRACT_SUMMARY
].map((doc: POLICY_DOCUMENT_TYPES) => {
  return {
    name: removeUnderscoresAndDashes(doc, true),
    value: doc
  };
});

const ShareDocumentModal = ({ policy, onClose, onProceed, isPaymentAgreement = false }: ShareDocumentModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { shareContennt } = useResource();

  return (
    <div className="w-full">
      <div className="p-5">
        <Formik
          initialValues={{
            type: isPaymentAgreement ? 'PAYMENT_AGREEMENT' : '',
            channel: '',
            email: policy?.applicant?.email,
            phone: getPhoneNumberFromShortCodeFormat(policy?.applicant?.phoneNumber ?? '')
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={Yup.object({
            channel: Yup.string().trim().required('Channel is required.')
          })}
          onSubmit={async (values) => {
            setLoading(true);
            const phone = getFormattedPhoneNumber(values.phone);
            const identifier =
              values.channel === 'sms' || values.channel === 'whatsapp' ? phone : values.email;
            const type = values.type;

            try {
              if (identifier) {
                const response = await sharePolicyAgreementDocumentLink(
                  policy?.ref,
                  type,
                  values.channel,
                  identifier
                );
                if (response) {
                  showToast.success(response?.message ?? 'Document shared successfully.');
                  shareContennt(response?.data);
                  onProceed?.(response?.data);
                }
              }
            } catch (error: any) {
              showToast.error(error?.description ?? 'Failed to share policy document.');
            } finally {
              setLoading(false);
            }
          }}>
          {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched }) => (
            <form className="" onSubmit={handleSubmit}>
              <div className={`${isPaymentAgreement ? 'hidden' : ''}`}>
                <SelectDropdown
                  label="Document name"
                  name="type"
                  listSelectedValue={values.type}
                  list={documentList}
                  onChange={(val: string | null) => setFieldValue('type', val)}
                  onBlur={handleBlur}
                  placeholder="Select document"
                  error={touched.type ? errors.type : undefined}
                  helperText={touched.type ? errors.type : undefined}
                  required
                  wrapperClasses="w-full"
                />
              </div>

              <div className={`${isPaymentAgreement ? '' : 'mt-5'}`}>
                <SelectDropdown
                  label="Channel"
                  name="channel"
                  listSelectedValue={values.channel}
                  list={[
                    { name: 'Email', value: 'email' },
                    { name: 'SMS', value: 'sms' },
                    { name: 'WhatsApp', value: 'whatsapp' }
                  ]}
                  onChange={(val: string | null) => setFieldValue('channel', val)}
                  onBlur={handleBlur}
                  placeholder="Select channel of transfer"
                  error={touched.channel ? errors.channel : undefined}
                  helperText={touched.channel ? errors.channel : undefined}
                  required
                  wrapperClasses="w-full"
                />
              </div>

              <div className="mt-5">
                {values.channel == 'email' && (
                  <Input
                    name="email"
                    label="Email"
                    value={values.email}
                    type="text"
                    wrapperClasses=""
                    required
                    onChange={handleChange}
                    onBlur={handleChange}
                    error={errors.email}
                    helperText={errors.email}
                  />
                )}
                {(values.channel == 'sms' || values.channel == 'whatsapp') && (
                  <Input
                    name="phone"
                    label="Phone"
                    value={values.phone}
                    type="tel"
                    wrapperClasses=""
                    onChange={handleChange}
                    onBlur={handleChange}
                    error={errors.phone}
                    helperText={errors.phone}
                    required
                  />
                )}
              </div>


              <div className="w-full flex gap-4 mt-6 !mb-4">
                <Button
                  label="Cancel"
                  onClick={onClose}
                  fullWidth
                  bgColor='secondary'
                />
                <Button
                  type="submit"
                  label="Submit"
                  fullWidth
                  loading={loading}
                  disabled={loading}
                  bgColor='primary'
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ShareDocumentModal;
